import {
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Output,
    Renderer2,
} from '@angular/core';
import { SegmentLabels } from '@se/cp.common';
import { IAnatomicMap } from '../../models/IAnatomicMap';
import { IArc } from '../../models/IArc';
import { IColorData } from '../../models/IColorData';
import { IPolyline } from '../../models/ILine';
import { IPoint } from '../../models/IPoint';
import { ISegmentItem } from '../../models/ISegmentItem';
import { MapDrawerService } from '../../services/mapDrawerService/mapDrawer.service';

@Directive({
    selector: '[anatomicMapDirective]',
})
export class AnatomicMapDirective {
    private actualMap: HTMLOrSVGElement;
    private maxCoordinates: IPoint;
    private svgEl: SVGElement;
    private difference = 50;

    private get viewBoxCoo(): IPoint {
        return {
            x: this.maxCoordinates.x + this.difference,
            y: this.maxCoordinates.y + this.difference
        }
    }

    @Output()
    public segmentSelected = new EventEmitter<string>();

    constructor(
        private drawer: MapDrawerService,
        private renderer: Renderer2,
        private svgElRef: ElementRef
    ) {
        this.svgEl = svgElRef.nativeElement;
        if (svgElRef.nativeElement.id === 'svg-icon-button') {
            alert();
        }
    }

    public loadOrUpdateMap(
        newMap: IAnatomicMap,
        data: IColorData[],
        segmentLabel: SegmentLabels,
        isWithTooltip: boolean
    ): void {
        if (this.actualMap) {
            this.renderer.removeChild(this.svgEl, this.actualMap);
        } else {
            // first time
            this.maxCoordinates = this.calculateMaxCoordinates(newMap);
            this.correctBoard();
        }
        this.actualMap = this.drawer.drawMap(newMap, data, segmentLabel, isWithTooltip, this.viewBoxCoo);
        this.addMapToView(this.actualMap);
    }

    private addMapToView(map: HTMLOrSVGElement) {
        this.renderer.appendChild(this.svgEl, map);
    }


    private correctBoard() {
        const { x, y } = this.viewBoxCoo;
        this.correctViewBox(x, y);
        this.correctMapText(x);
    }

    private correctViewBox(x: number, y: number) {
        this.renderer.setAttribute(this.svgEl, 'viewBox', `0 0 ${x} ${y}`);
    }

    private correctMapText(size: number) {
        const minFontSize = 10;
        const maxFontSize = 24;
        const fontsizeCalc=  Math.min(Math.max(Math.round(size / 18),minFontSize), maxFontSize) + 'px';
        this.renderer.setAttribute(
            this.svgElRef.nativeElement,
            'font-size',
            fontsizeCalc
        );
    }

    private calculateMaxCoordinates(map: IAnatomicMap): IPoint {
        const output = { x: 50, y: 50 };
        map.segmentCollection.forEach((seg: ISegmentItem) => {
            seg.pieces.forEach((p) => {
                if ('center' in p) {
                    this.checkArcMaxCoo(p, output);
                } else {
                    this.checkLineMaxCoo(p, output);
                }
            });
        });
        return output;
    }

    private checkArcMaxCoo(piece: IArc, actual: IPoint): void {
        this.substituteValueIfSmaller(piece.center, actual);
        this.substituteValueIfSmaller(piece.center, actual);
        this.substituteValueIfSmaller(piece.start, actual);
        this.substituteValueIfSmaller(piece.start, actual);
        if (piece.end) {
            this.substituteValueIfSmaller(piece.end, actual);
            this.substituteValueIfSmaller(piece.end, actual);
        }
    }

    private checkLineMaxCoo(piece: IPolyline, actual: IPoint): void {
        piece.points.forEach((p) => {
            this.substituteValueIfSmaller(p, actual);
            this.substituteValueIfSmaller(p, actual);
        });
    }

    private substituteValueIfSmaller(newV: IPoint, OldV: IPoint): void {
        if (newV.x > OldV.x) {
            OldV.x = newV.x;
        }
        if (newV.y > OldV.y) {
            OldV.y = newV.y;
        }
    }

    // @HostListener('click', ['$event'])
    // private _(e: Event){
    //     e.stopPropagation();
    //     this.onAnatomicMapClick();
    // }

    public onAnatomicMapClick() {
        const clickPoint = this.svgEl.querySelector('circle') as SVGCircleElement;
        if (clickPoint) {
            this.segmentSelected.emit(clickPoint.getAttribute('fatherId'));
            clickPoint.remove();
        } else {
            this.segmentSelected.emit(null);
        }
    }
}
